import { useEffect, useMemo, useState } from 'react';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import { useDebounce } from '../hooks';
import { ProdutosService } from '../services/api/estoque/produtos/ProdutosService';

type TAutoCompleteOption = {
  idprod: string;
  label: string;
  descricao: string;
  referencia: string;
  marca: string;
  precoSaida: number;
  precoVenda: number;
  un: string;
  idproduto: string;
};

interface IAutoCompleteProdutoProps {
  isExternalLoading?: boolean;
  filtro?: string;
  valueId?: string;
  referencia?: (novoTextoselect: string | undefined) => void;
  marca?: (novoTextoselect: string | undefined) => void;
  descricao?: (novoTextoselect: string | undefined) => void;
  vencimento?: (novoTextoselect: boolean | undefined) => void;
  precoSaida?: (novoTextoselect: number | undefined) => void;
  precoVenda?: (novoTextoselect: number | undefined) => void;
  un?: (novoTextoselect: string | undefined) => void;
  idprod?: (novoTextoselect: string | undefined) => void;
}

export const AutoCompleteProduto1: React.FC<IAutoCompleteProdutoProps> = ({
  isExternalLoading = false,
  referencia,
  marca,
  descricao,
  precoSaida,
  precoVenda,
  un,
  idprod,
  valueId,
  filtro = 'descricao',
}) => {
  const { debounce } = useDebounce();
  const [selectedId, setSelectedId] = useState<string | undefined>();
  const [opcoes, setOpcoes] = useState<TAutoCompleteOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [busca, setBusca] = useState('');
  useEffect(() => {
    if (valueId) {
      setSelectedId(valueId);
      setBusca(''); // opcional, evita reabrir o autocomplete
    }
  }, [valueId]);
  
  useEffect(() => {
    setIsLoading(true);
    debounce(() => {
      ProdutosService.getAlllist(busca, selectedId, filtro).then(
        (result) => {
          setIsLoading(false);
          if (result instanceof Error) {
            // alert(result.message);
          } else {
            if (filtro === 'descricao') {
              setOpcoes(
                result.data.map((produto) => ({
                  idprod: produto.idproduto,
                  label: produto.descricao + ' - ' + produto.referencia,
                  referencia: produto.referencia,
                  marca: produto.marca,
                  descricao: produto.descricao,
                  vencimento: produto.vencimento,
                  un: produto.un,
                  precoSaida: produto.precosaida,
                  precoVenda: produto.pvenda,
                  idproduto: produto.idproduto,
                })),
              );
            } else if (filtro === 'codbarraentrada') {
              setOpcoes(
                result.data.map((produto) => ({
                  idprod: produto.idproduto,
                  label: produto.codbarraentrada,
                  referencia: produto.referencia,
                  marca: produto.marca,
                  descricao: produto.descricao,
                  vencimento: produto.vencimento,
                  precoSaida: produto.precosaida,
                  un: produto.un,
                  precoVenda: produto.pvenda,
                  idproduto: produto.idproduto,
                })),
              );
            } else if (filtro === 'referencia') {
              setOpcoes(
                result.data.map((produto) => ({
                  idprod: produto.idproduto,
                  label: produto.descricao + ' - ' + produto.referencia,
                  referencia: produto.referencia,
                  descricao: produto.descricao,
                  marca: produto.marca,
                  vencimento: produto.vencimento,
                  precoSaida: produto.precosaida,
                  un: produto.un,
                  precoVenda: produto.pvenda,
                  idproduto: produto.idproduto,
                })),
              );
            } else if (filtro === '') {
              setOpcoes(
                result.data.map((produto) => ({
                  idprod: produto.idproduto,
                  label: produto.descricao + ' - ' + produto.referencia,
                  referencia: produto.referencia,
                  descricao: produto.descricao,
                  marca: produto.marca,
                  vencimento: produto.vencimento,
                  precoSaida: produto.precosaida,
                  un: produto.un,
                  precoVenda: produto.pvenda,
                  idproduto: produto.idproduto,
                })),
              );
            }
          }
        },
      );
    });
  }, [busca, selectedId, filtro]);

  const autoCompleteSelectedOption = useMemo(() => {
    if (!selectedId) return null;

    const selectedOption = opcoes.find((opcao) => opcao.idprod === selectedId);
    if (!selectedOption) return null;

    return selectedOption;
  }, [selectedId, opcoes]);

  return (
    <Autocomplete
    openText="Abrir"
    closeText="Fechar"
    noOptionsText="Sem opções"
    loadingText="Carregando..."
    disablePortal
    options={opcoes}
    loading={isLoading}
    disabled={isExternalLoading}
    value={autoCompleteSelectedOption}
    getOptionLabel={(option) => option?.label ?? ''}
    isOptionEqualToValue={(option, value) =>
      option.idprod === value.idprod
    }
    onInputChange={(_, newValue, reason) => {
      if (reason === 'input') {
        setBusca(newValue);
      }
    }}
    onChange={(_, newValue) => {
      setSelectedId(newValue?.idprod);
      idprod?.(newValue?.idprod);
      un?.(newValue?.un);
      marca?.(newValue?.marca);
      referencia?.(newValue?.referencia);
      descricao?.(newValue?.descricao);
      precoSaida?.(newValue?.precoSaida);
      precoVenda?.(newValue?.precoVenda);
      setBusca('');
    }}
    popupIcon={isLoading ? <CircularProgress size={28} /> : undefined}
    renderInput={(params) => (
      <TextField {...params} size="small" label="Pesquisar ítem" />
    )}
  />
  
  );
};
